import React, { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import {
  RoutineEditScreenProps,
  RoutineScheduleDaily,
  RoutineScheduleMonthly,
  RoutineScheduleWeekly,
} from "../RoutineEditNavigation";
import { RoutineEditScheduleCardComponent } from "./RoutineEditScheduleCardComponent";
import { getControlType } from "../RoutineEditControlTypeCard/getControlType";
import {
  RoutineEditScheduleCard_Routine$data,
  RoutineEditScheduleCard_Routine$key,
} from "./__generated__/RoutineEditScheduleCard_Routine.graphql";
import { RoutineEditScheduleCardNoSchedule } from "./RoutineEditScheduleCardNoSchedule";
import { useRoutineEditState } from "../../State/RoutineEditState";
import { ALL_WEEK_NUMBERS } from "./WeekNumberPickerDialog";

function remoteScheduleToLocal(
  remoteSchedule: RoutineEditScheduleCard_Routine$data["currentSchedule"]
):
  | RoutineScheduleDaily
  | RoutineScheduleWeekly
  | RoutineScheduleMonthly
  | undefined {
  switch (remoteSchedule?.__typename) {
    case "RoutineScheduleDaily":
      return {
        freq: "daily",
        isAllDay: remoteSchedule.isAllDay ?? false,
        deadlineDuration: remoteSchedule.deadlineDuration,
        weekdays: remoteSchedule.weekdays.map((weekday) => ({
          weekday: weekday.weekday,
          startTime: weekday.startTime,
          originalTaskImportance: weekday.tasks.reduce(
            (acc, cur) => {
              acc[cur.taskId] = cur.importance;
              return acc;
            },
            {} as RoutineScheduleDaily["weekdays"][number]["originalTaskImportance"]
          ),
          taskImportanceChanges: {},
        })),
      };
    case "RoutineScheduleWeekly":
      return {
        freq: "weekly",
        startWeekday: remoteSchedule.startWeekday ?? 0,
        endWeekday: remoteSchedule.endWeekday ?? 6,
        weeks:
          remoteSchedule.weeks.length === 0
            ? ALL_WEEK_NUMBERS
            : remoteSchedule.weeks.slice(),
      };
    case "RoutineScheduleMonthly": {
      return {
        freq: "monthly",
        startDay: remoteSchedule.startDay,
        endDay: remoteSchedule.endDay ?? remoteSchedule.startDay,
        months: remoteSchedule.months.slice(),
      };
    }
    case undefined:
      return undefined;
  }
}

export const RoutineEditScheduleCard = (
  props: {
    routine: RoutineEditScheduleCard_Routine$key;
  } & RoutineEditScreenProps<"RoutineEditMain">
) => {
  const routine = useFragment(
    graphql`
      fragment RoutineEditScheduleCard_Routine on Routine {
        id
        title
        type
        activeVersion {
          tasks {
            id
            title
            __typename
            ... on TaskCheck {
              sentiments
            }
          }
        }

        currentSchedule {
          __typename
          ... on RoutineScheduleDaily {
            deadlineDuration
            isAllDay
            hasAdvancedScheduling
            weekdays {
              weekday
              startTime {
                hour
                minute
              }
              tasks {
                importance
                taskId
              }
            }
          }
          ... on RoutineScheduleWeekly {
            startWeekday
            endWeekday
            weeks
          }
          ... on RoutineScheduleMonthly {
            startDay
            endDay
            months
          }
        }
      }
    `,
    props.routine
  );
  const params = useRoutineEditState();

  const controlType = useMemo(
    () =>
      params.controlType ??
      getControlType(
        routine?.activeVersion?.tasks.map((t) => ({
          title: t.title,
          type: t.__typename,
          sentiments: t.sentiments,
        })) ?? []
      ),
    [params.controlType, routine?.activeVersion?.tasks]
  );

  const localSchedule = useMemo(() => {
    switch (params.scheduleFreq) {
      case "daily":
        return params.scheduleDaily;
      case "weekly":
        return params.scheduleWeekly;
      case "monthly":
        return params.scheduleMonthly;
      default:
        return undefined;
    }
  }, [
    params.scheduleDaily,
    params.scheduleFreq,
    params.scheduleMonthly,
    params.scheduleWeekly,
  ]);

  const selectedSchedule = useMemo(
    () => localSchedule ?? remoteScheduleToLocal(routine.currentSchedule),
    [localSchedule, routine.currentSchedule]
  );

  const onOpenTaskImportance = useCallback(() => {
    if (selectedSchedule?.freq === "daily") {
      props.navigation.navigate("RoutineEditScheduleDailyTaskImportance", {
        id: routine.id,
      });
    }
  }, [props.navigation, routine.id, selectedSchedule?.freq]);

  if (selectedSchedule) {
    return (
      <RoutineEditScheduleCardComponent
        selectedSchedule={selectedSchedule}
        onOpenTaskImportance={onOpenTaskImportance}
        controlType={controlType}
        hasAdvancedScheduling={
          routine.currentSchedule?.__typename === "RoutineScheduleDaily" &&
          routine.currentSchedule.hasAdvancedScheduling
        }
      />
    );
  }

  if (routine.type === "TEMPLATE") {
    return null;
  }

  return <RoutineEditScheduleCardNoSchedule />;
};
